import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSelector } from '../components/LanguageSelector';
import { Button } from '../components/ui/button';
import { AvatarContainer } from '../components/AvatarContainer';
import { 
  MessageSquare, 
  Mic, 
  Trophy, 
  Stethoscope,
  ArrowRight,
  BookOpen,
  Users,
  Star
} from 'lucide-react';

export default function Landing() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_egns-deutsch/artifacts/wljgvqe2_copy_DF94F79F-AD21-40BD-8A6F-78D3B8D2676A.jpeg" 
                alt="EGNS Logo" 
                className="h-10 w-auto"
              />
            </div>
            
            <div className="flex items-center gap-4">
              <LanguageSelector variant="ghost" />
              <Link to="/login">
                <Button variant="ghost" data-testid="login-btn">{t('login')}</Button>
              </Link>
              <Link to="/register">
                <Button data-testid="register-btn">{t('register')}</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-slate-900 min-h-screen pt-24 pb-16 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-8rem)]">
            {/* Left: Text content */}
            <div className="stagger-children text-white">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                <span className="text-sm">🇪🇬</span>
                <span className="text-sm font-medium">Egyptian-German Nursing School</span>
                <span className="text-sm">🇩🇪</span>
              </div>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                {t('heroTitle')}
              </h1>
              
              <p className="text-lg sm:text-xl text-white/80 mb-8 max-w-lg">
                {t('heroSubtitle')}
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button 
                    size="lg" 
                    className="btn-primary bg-teal-500 hover:bg-teal-600 text-white font-semibold px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-teal-500/25 transition-all hover:-translate-y-1"
                    data-testid="hero-get-started"
                  >
                    {t('getStarted')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-full"
                >
                  {t('learnMore')}
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-white/20">
                <div>
                  <p className="font-mono text-3xl font-bold text-teal-400">A1-B2</p>
                  <p className="text-white/60 text-sm mt-1">CEFR Levels</p>
                </div>
                <div>
                  <p className="font-mono text-3xl font-bold text-amber-400">40+</p>
                  <p className="text-white/60 text-sm mt-1">Lessons</p>
                </div>
                <div>
                  <p className="font-mono text-3xl font-bold text-teal-400">2</p>
                  <p className="text-white/60 text-sm mt-1">AI Teachers</p>
                </div>
              </div>
            </div>

            {/* Right: Teacher avatars */}
            <div className="relative flex justify-center lg:justify-end">
              <div className="relative">
                {/* Frau Rasha - foreground */}
                <div className="relative z-20 transform hover:-translate-y-2 transition-transform duration-300">
                  <AvatarContainer 
                    teacher="frau_rasha" 
                    size="xl" 
                    className="shadow-2xl shadow-teal-500/20"
                  />
                </div>
                
                {/* Herr Amr - background */}
                <div className="absolute -top-8 -right-8 z-10 transform hover:-translate-y-2 transition-transform duration-300">
                  <AvatarContainer 
                    teacher="herr_amr" 
                    size="lg" 
                    className="shadow-2xl shadow-slate-500/20 opacity-90"
                  />
                </div>
                
                {/* Decorative elements */}
                <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-amber-500/20 rounded-full blur-2xl" />
                <div className="absolute -top-4 -right-4 w-32 h-32 bg-teal-500/20 rounded-full blur-2xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">{t('features')}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Everything you need to master German, powered by cutting-edge AI technology
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* AI Teachers */}
            <div className="bento-item group cursor-pointer" data-testid="feature-ai-teachers">
              <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <MessageSquare className="h-6 w-6 text-teal-500" />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-2">{t('aiTeachers')}</h3>
              <p className="text-muted-foreground text-sm">{t('aiTeachersDesc')}</p>
            </div>

            {/* Speech Recognition */}
            <div className="bento-item group cursor-pointer" data-testid="feature-speech">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Mic className="h-6 w-6 text-amber-500" />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-2">{t('speechRecognition')}</h3>
              <p className="text-muted-foreground text-sm">{t('speechRecognitionDesc')}</p>
            </div>

            {/* Gamification */}
            <div className="bento-item group cursor-pointer" data-testid="feature-gamification">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Trophy className="h-6 w-6 text-purple-500" />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-2">{t('gamification')}</h3>
              <p className="text-muted-foreground text-sm">{t('gamificationDesc')}</p>
            </div>

            {/* Medical German */}
            <div className="bento-item group cursor-pointer" data-testid="feature-medical">
              <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Stethoscope className="h-6 w-6 text-red-500" />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-2">{t('medicalGerman')}</h3>
              <p className="text-muted-foreground text-sm">{t('medicalGermanDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* About EGNS Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image Side */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_egns-deutsch/artifacts/a4mqn4d5_IMG_3118.jpeg" 
                  alt="EGNS - Egyptian German Nursing School" 
                  className="w-full h-auto"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-teal-500/20 rounded-full blur-2xl" />
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-amber-500/20 rounded-full blur-2xl" />
            </div>

            {/* Text Side */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 bg-teal-500/10 rounded-full px-4 py-2">
                <span className="text-teal-600 font-semibold text-sm">About EGNS</span>
              </div>
              
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">
                The Right Start Begins Here
              </h2>
              
              <div className="space-y-4 text-slate-600">
                <p className="text-lg leading-relaxed">
                  The Egyptian-German Nursing School (EGNS) is a pioneering institution bridging 
                  Egyptian healthcare talent with German medical excellence.
                </p>
                <p className="leading-relaxed">
                  Our AI-powered German language app is designed specifically for nursing students, 
                  combining conversational practice with medical terminology to prepare you for 
                  a successful healthcare career in Germany.
                </p>
              </div>

              {/* Tagline in three languages */}
              <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-6 border border-slate-200">
                <div className="space-y-2 text-center">
                  <p className="text-xl font-semibold text-amber-600" dir="rtl">
                    البداية الصح تبدأ من هنا – EGNS
                  </p>
                  <p className="text-lg font-medium text-slate-700">
                    The right start begins here – EGNS
                  </p>
                  <p className="text-lg font-medium text-slate-600 italic">
                    Der richtige Start beginnt hier – EGNS
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-teal-500/10 flex items-center justify-center">
                    <Users className="h-5 w-5 text-teal-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">500+</p>
                    <p className="text-xs text-slate-500">Students</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                    <BookOpen className="h-5 w-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">40+</p>
                    <p className="text-xs text-slate-500">Lessons</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                    <Trophy className="h-5 w-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">95%</p>
                    <p className="text-xs text-slate-500">Success Rate</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Levels Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">CEFR Levels</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Progress through internationally recognized language levels
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { level: 'A1', name: 'Beginner', color: 'teal', lessons: 10 },
              { level: 'A2', name: 'Elementary', color: 'blue', lessons: 12 },
              { level: 'B1', name: 'Intermediate', color: 'purple', lessons: 10 },
              { level: 'B2', name: 'Upper Intermediate', color: 'amber', lessons: 8 },
            ].map((item) => (
              <div 
                key={item.level}
                className="level-card bg-card rounded-xl p-6 border border-border"
                data-testid={`level-${item.level}`}
              >
                <div className={`w-16 h-16 rounded-full bg-${item.color}-500/10 flex items-center justify-center mb-4`}>
                  <span className={`font-heading font-bold text-2xl text-${item.color}-500`}>{item.level}</span>
                </div>
                <h3 className="font-heading font-semibold text-lg mb-1">{item.name}</h3>
                <p className="text-muted-foreground text-sm">{item.lessons} lessons</p>
                <div className="mt-4 flex items-center gap-1">
                  {[...Array(4)].map((_, i) => (
                    <Star 
                      key={`${item.name}-star-${i}`} 
                      className={`h-4 w-4 ${i < ['A1', 'A2', 'B1', 'B2'].indexOf(item.level) + 1 ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-egns-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 hero-pattern opacity-50" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Ready to Start Your German Journey?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Join EGNS students who are mastering German with AI-powered lessons. Start learning today!
          </p>
          <Link to="/register">
            <Button 
              size="lg"
              className="bg-teal-500 hover:bg-teal-600 text-white font-semibold px-8 py-6 text-lg rounded-full"
              data-testid="cta-get-started"
            >
              {t('getStarted')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_egns-deutsch/artifacts/wljgvqe2_copy_DF94F79F-AD21-40BD-8A6F-78D3B8D2676A.jpeg" 
                alt="EGNS Logo" 
                className="h-8 w-auto"
              />
            </div>
            <p className="text-muted-foreground text-sm">
              © 2026 Egyptian-German Nursing School. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <span className="text-2xl">🇪🇬</span>
              <span className="text-2xl">🇩🇪</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
